import type { Anamnesis, UpdateAnamnesisPayload } from '../../types/index.js';
import { baseApi } from '../base-api.js';

type AnamnesisResponse = { anamnesis: Anamnesis };
type AnamnesisMutationResponse = { anamnesis: Anamnesis; message: string };

/**
 * Mirrors `apps/server/src/modules/anamnesis` (`anamnesisController`) — kept as its own file
 * since the server keeps it as its own module (`anamnesisService`, separate entities), even
 * though the client-side `anamnesis` and `patient` features are being merged into one domain.
 * Tagged by `patient_id` (anamnesis is 1:1 with a patient, no list route exists). Anamnesis
 * mutations never touch the Patient entity, so nothing here invalidates `Patient` tags. The
 * reverse is asymmetric: `anamnesis.patient_id` is ON DELETE CASCADE, so `deletePatient` (in
 * `patient.api.ts`) invalidates the deleted patient's `Anamnesis` tag.
 */
export const anamnesisApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAnamnesisByPatientId: builder.query<Anamnesis, string>({
      query: (patientId) => ({ path: ['anamnesis', { param: 'patient_id', value: patientId }], method: 'get' }),
      transformResponse: (response: AnamnesisResponse) => response.anamnesis,
      providesTags: (_result, _error, patientId) => [{ type: 'Anamnesis', id: patientId }],
    }),

    updateAnamnesis: builder.mutation<Anamnesis, UpdateAnamnesisPayload>({
      query: (payload) => ({
        path: ['anamnesis', { param: 'patient_id', value: payload.patient_id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: AnamnesisMutationResponse) => response.anamnesis,
      invalidatesTags: (_result, _error, { patient_id }) => [{ type: 'Anamnesis', id: patient_id }],
    }),
  }),
});

export const { useGetAnamnesisByPatientIdQuery, useUpdateAnamnesisMutation } = anamnesisApi;
